
/* -----------------------------------------------------------------------------------------------------
  - Created: [20-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains WorksiteReportTable using MUI components
  ------------------------------------------------------------------------------------------------------- */

import React, { useEffect, useState } from 'react';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import WorksiteReportFilter from '../WorksiteReportFilter/WorksiteReportFilter';
import { handlePaginationEntries } from '../../../../utils/pagination';
import { get_worksite_reports_with_pagination } from '../../../../config/ReportApis/worksiteReportApis';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import Loader from '../../../../Components/Loader/Loader';
import useDataExport from '../../../../utils/useDataExport';

function WorksiteReportTable() {

    const [siteReportList, setSiteReportList] = useState([]);

    // Calculate the total pages based on the number of items and items per page
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });

    const [filterValues, setFilterValues] = useState({}); // Define filter values state
    const [isLoading, setIsLoading] = useState(false); // Loader state


    // Columns for the table
    const columns = [
        { title: "#", key: "key" },
        { title: "Project", key: "project" },
        { title: "Worksite", key: "worksite" },
        { title: "Schedule Code", key: "ScheduleCode" },
        { title: "Technology name", key: "technologiname" },
        { title: "Schedule Date", key: "ScheduleDate" },
        { title: "FSE", key: "fse_names" },
        { title: "Status", key: "survey_status" },
        { title: "Total Mandays", key: "total_mandays" },

    ]

    // Map the schedule data into table rows
    const rows = () => siteReportList?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        project: item?.projectName,
        worksite: item.worksite,
        ScheduleCode: item?.scheduleCode,
        ScheduleDate: item.scheduleDate,
        survey_status: item?.survey_status,
        technologiname: item.technologiname,
        fse_names: item.fse_names,
        total_mandays: item?.total_mandays,
    }));

    // Fetch schedule data on page change or refresh
    useEffect(() => {
        getWorksiteReport();
    }, [pageNumber, totalItemPerPage, filterValues]);


    // Fetch schedule data from API
    const getWorksiteReport = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        // Append filter values to formData
        Object.keys(filterValues).forEach(key => formData.append(key, filterValues[key]));

        try {
            setIsLoading(true)
            const response = await get_worksite_reports_with_pagination(formData);
            if (response?.status === "success") {
                const data = response?.data;
                setSiteReportList(data?.data);
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));
            } else {
                setSiteReportList([]);
            }
        } catch (error) {

        } finally {
            setIsLoading(false)
        }
    };

    // Handle search filter change
    const handleSearchFilter = (filterValues) => {
        setFilterValues(filterValues);
    };

    // Handle pagination changes
    const handlePageChange = (pageNumber) => setPageNumber(pageNumber);
    const handleItemPageCountChange = (pageItemCount) => setTotalItemPerPage(pageItemCount);

    // Define table headers (same order as columns)
    const headers = columns.map(col => col.title);

    // Map function for data export
    const mapDataFn = (item, index) => [
        index + 1, // Equivalent of key / serial number
        item?.projectName || "—",
        item?.worksite || "—",
        item?.scheduleCode || "—",
        item?.technologiname || "—",
        item?.scheduleDate || "—",
        item?.fse_names || "—",
        item?.survey_status || "—",
        item?.total_mandays ?? 0,



    ];

    // Reusable export handler (using your custom hook)
    const {
        handleExportExcel,
        handleExportPDF,
        isExportingExcel,
        isExportingPDF,
    } = useDataExport(
        get_worksite_reports_with_pagination,
        mapDataFn,
        headers,
        "Worksite Report"
    );
    return (
        <div className='report-table-container'>
            <WorksiteReportFilter
                tableTitle="Worksite Report"
                searchFilter={handleSearchFilter}

                on_change_value={handlePageChange}
                on_page_count_update={handleItemPageCountChange}
                onExportPDF={() => handleExportPDF(filterValues, { page: 1, pageItemCount: pagination?.totalEntries })}
                onExportExcel={() => handleExportExcel(filterValues, { page: 1, pageItemCount: pagination?.totalEntries })}
                isExportingPDF={isExportingPDF}
                isExportingExcel={isExportingExcel}


            />
            <div className="table-container">
                <CustomTable
                    columns={columns}
                    rows={isLoading ? [] : rows()}
                    isCheckbox={false}
                    isLoading={isLoading}
                />
            </div>
            <div className='pagination-container'>
                {isLoading ? <Loader /> : <CustomBottomPagination
                    items_per_page={totalItemPerPage}
                    current_page={pageNumber}
                    total_page_count={totalPages}
                    on_change_value={handlePageChange}
                    on_page_count_update={handleItemPageCountChange}
                    pagination={pagination}
                />}
            </div>
        </div>
    );
}

export default WorksiteReportTable;
